import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FieldArrayType, FormlyModule } from '@ngx-formly/core';
import {} from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { UserRolesService } from '../../../features/admin/user-roles/user-roles.service';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { UserStore } from 'app/core/services/user.store';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { commitUserValue } from './user-edit.util';

@Component({
  selector: 'formly-repeat-section-reorder',
  template: `
    <div class="my-4" [attr.data-cy]="field.props?.keyPath ?? null">
      @if (props.label && !props.hideLabel) {
        @if (props.labelType === undefined || props.labelType === 'titel') {
          <h3 class="font-bold mb-2">
            {{ props.label }}
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </h3>
        } @else {
          <label class="block text-sm font-medium text-default mb-2">
            {{ props.label }}
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </label>
        }
      }
      @if (props.description) {
        <p class="mb-2">{{ props.description }}</p>
      }

      @if (field?.fieldGroup?.length >= 1) {
        <div
          class="inline-block w-full"
          cdkDropList
          [cdkDropListEnterPredicate]="denyForeignDrops"
          (cdkDropListDropped)="drop($event)">
          <!-- Object identity, not field.id: formly never regenerates an id it finds on a config,
               so a stored generatedForm carrying a serialized id would give every cloned row the
               same track key (NG0955) — same rationale as repeat.component.ts. -->
          @for (fieldGroupField of field.fieldGroup; let i = $index; track fieldGroupField) {
            <div
              cdkDrag
              [cdkDragDisabled]="disableDrag || form.disabled || field.props.disabled || hideAddButton()"
              class="repeater-item flex">
              @if (field.className !== 'hidden') {
                @if (!disableDrag && !form.disabled && !field.props.disabled && !hideAddButton()) {
                  <mat-icon
                    class="flex-0 text-light cursor-move mt-2 mr-2"
                    cdkDragHandle
                    svgIcon="mat_outline:drag_indicator"></mat-icon>
                } @else {
                  <div>{{ i + 1 }}. &nbsp;</div>
                }

                <div class="flex-1 min-w-0">
                  <formly-field class="flex-1" [field]="fieldGroupField"></formly-field>
                </div>
                @if (
                  !form.disabled &&
                  !fieldGroupField.props.disabled &&
                  (props.minCount ?? 0) < field.fieldGroup.length &&
                  !hideAddButton()
                ) {
                  <button type="button" class="ml-2 flex-0 max-h-fit " mat-button (click)="remove(i)">
                    <mat-icon class="icon-size-4">close</mat-icon>
                  </button>
                }
              }
            </div>
          }
        </div>
      } @else {
        <p class="text-sm text-accent">Geen {{ props.label ? props.label.toLowerCase() : 'items' }} aangemaakt</p>
      }
      <div class="py-4">
        @if (!form.disabled && !field.props.disabled && !hideAddButton()) {
          <button type="button" class="small-button dd-action-button" mat-flat-button [attr.aria-label]="props.addText || 'Toevoegen'" (click)="add(field.fieldGroup?.length, getDefaultModelForIndex(field.fieldGroup?.length))">
            <mat-icon>add</mat-icon>
            {{ props.addText }}
          </button>
        }
      </div>
    </div>
  `,
  imports: [
    FormlyModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    InfoTooltipComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .custom-placeholder {
      background: #ccc;
      border: dotted 3px #999;
      min-height: 60px;
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }
  `,
})
export class FormlyRepeatReorderTypeComponent extends FieldArrayType implements OnInit {
  disableDrag: boolean = true;
  userStore = inject(UserStore);
  userRolesService = inject(UserRolesService);
  formService = inject(FormService);
  hideAddButton = signal(false);

  // Inside the form builder this list joins the page-wide cdkDropListGroup; never accept
  // items from other lists — this component only supports reordering its own rows.
  readonly denyForeignDrops = (drag: CdkDrag, drop: CdkDropList): boolean => drag.dropContainer === drop;

  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer !== event.container) {
      return;
    }
    const modelCopy = this.model;
    moveItemInArray(modelCopy, event.previousIndex, event.currentIndex);
    commitUserValue(this.field.formControl, modelCopy);
    this.options.build(this.field);
  }

  trackByFn(index: number, item: any): any {
    return item.id;
  }

  ngOnInit() {
    const moduleId = this.options.formState?.entity?.id;
    if (this.props.forceReorder || moduleId == null) {
      // Design-time editors (e.g. the form builder config panel) pass forceReorder: reordering
      // config rows is gated by having form-edit access to open the builder, not by the target
      // module's entry-edit permission (which can be false for type:'form' modules).
      this.disableDrag = false;
    } else {
      this.disableDrag = !this.userRolesService.checkIfHasEditPermission(
        this.userStore.currentUserPermissions(),
        moduleId
      );
    }

    if (this.props.initialCount === undefined) {
      this.props.initialCount = 0;
    }
    if (this.props.hideAddButtonForRoles != null && Object.keys(this.props.hideAddButtonForRoles).length > 0) {
      this.hideAddButton.set(this.field.props.hideAddButtonForRoles[this.userStore.currentUserId()]);
    }
    this.validateLength();
  }

  /**
   * Get the default model for a specific index, delegating to FormService.
   */
  getDefaultModelForIndex(index: number): any {
    return this.formService.getDefaultModelForIndex(this.field, index);
  }

  /**
   * Apply defaultCount only for NEW entries (when there's no existing data).
   * If there's already saved data, we just load that - defaultCount is not enforced.
   */
  private validateLength() {
    // Only apply defaultCount for new entries — not for edits.
    if (!this.options?.formState?.isNew || this.model?.length > 0) {
      return;
    }

    const targetCount = this.props.defaultCount ?? this.props.initialCount;
    // Capture starting length BEFORE the loop - fieldGroup.length changes after each add()
    const startingLength = this.field.fieldGroup?.length ?? 0;

    if (startingLength < targetCount) {
      const itemsToAdd = targetCount - startingLength;
      for (let i = 0; i < itemsToAdd; i++) {
        const currentIndex = startingLength + i;
        this.add(currentIndex, this.getDefaultModelForIndex(currentIndex));
      }
    }
  }
}
